import { ASSISTENT_API_HEADER_KEY, ASSISTENT_API_URL } from '@/config/backend'
import axios from 'axios'
import { jwtDecode } from 'jwt-decode'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { logger } from '@/lib/logger'

interface DecodedToken {
  id: string
  sub: string
  email: string
  firstname?: string
  lastname?: string
  role: string
  roles: string[]
  exp: number
}

export interface User {
  id: string
  email: string
  name?: string
  firstname?: string
  lastname?: string
  avatar?: string
  role: string
  roles: string[]
}

interface AuthState {
  token: string | null
  tokenExpires: string | null
  refreshToken: string | null
  refreshTokenExpires: string | null
  decodedToken: DecodedToken | null
  user: User | null

  // Actions
  userLogin: (payload: { email: string; password: string }) => Promise<void>
  userLogout: () => void
  userAutoLogin: () => Promise<void>
  refreshAuthToken: () => Promise<void>
  setToken: (token: string, tokenExpires: string, refreshToken: string, refreshTokenExpires: string) => void
  clearToken: () => void

  // Getters
  getApiHeaders: () => Record<string, string>
  getApiHeadersWithoutBaseContentType: () => Record<string, string>
  isAuthenticated: () => boolean
  getToken: () => string | null
  userEmail: () => string
  getUserId: () => string
  getFirstName: () => string
  getLastName: () => string
  getEmail: () => string
  isCustomer: () => boolean
  isAdmin: () => boolean
  hasRole: (role: string) => boolean
  canSeeDeviceId: () => boolean
  canEdit: () => boolean
}

let timer: NodeJS.Timeout

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      tokenExpires: null,
      refreshToken: null,
      refreshTokenExpires: null,
      decodedToken: null,
      user: null,

      setToken: (token: string, tokenExpires: string, refreshToken: string, refreshTokenExpires: string) => {
        const decodedToken = jwtDecode<DecodedToken>(token)
        const user: User = {
          id: decodedToken.id,
          email: decodedToken.email,
          name: `${decodedToken.firstname || ''} ${decodedToken.lastname || ''}`.trim() || decodedToken.email,
          firstname: decodedToken.firstname,
          lastname: decodedToken.lastname,
          role: decodedToken.role,
          roles: decodedToken.roles,
        }

        set({
          token,
          tokenExpires,
          refreshToken,
          refreshTokenExpires,
          decodedToken,
          user,
        })

        localStorage.setItem("token", token)
        localStorage.setItem("tokenExpires", tokenExpires)
        localStorage.setItem("refreshToken", refreshToken)
        localStorage.setItem("refreshTokenExpires", refreshTokenExpires)

        const timeLeft = Number(tokenExpires) - new Date().getTime() - 30000 // 30 Sekunden vorher
        clearTimeout(timer)
        timer = setTimeout(() => {
          get().refreshAuthToken()
        }, timeLeft)
      },

      clearToken: () => {
        set({
          token: null,
          tokenExpires: null,
          refreshToken: null,
          refreshTokenExpires: null,
          decodedToken: null,
          user: null,
        })

        localStorage.removeItem("token")
        localStorage.removeItem("tokenExpires")
        localStorage.removeItem("refreshToken")
        localStorage.removeItem("refreshTokenExpires")

        clearTimeout(timer)

        // Reset other stores would be handled by store composition
      },

      userLogin: async (payload: { email: string; password: string }) => {
        const url = ASSISTENT_API_URL + "token/authenticate"

        const userLoginDo = {
          userId: payload.email,
          password: payload.password
        }

        try {
          const response = await axios.post(url, userLoginDo, {
            headers: {
              'X-AAI-APPLICATIONID': ASSISTENT_API_HEADER_KEY
            }
          })

          get().setToken(
            response.data.token,
            response.data.tokenExpire,
            response.data.refreshToken,
            response.data.refreshTokenExpire
          )

        } catch (error) {
          throw new Error('UNKNOWN_ERROR')
        }
      },

      userLogout: () => {
        get().clearToken()
        // Navigation would be handled by React Router
        window.location.href = '/login'
      },

      userAutoLogin: async () => {
        const token = localStorage.getItem("token")
        const expiresIn = localStorage.getItem("tokenExpires")
        const isDevBypass = localStorage.getItem('dev_bypass_mode') === 'true'

        // In dev bypass mode, skip timing logic and just set token
        if (isDevBypass && token) {
          logger.auth('Dev Bypass: Auto-login with dev token')
          get().setToken(
            token,
            expiresIn || String(Date.now() + 24 * 60 * 60 * 1000), // 24 hours from now
            localStorage.getItem("refreshToken") || 'dev-refresh',
            localStorage.getItem("refreshTokenExpires") || String(Date.now() + 24 * 60 * 60 * 1000)
          )

          // Initialize configuration after auto-login
          try {
            const { useConfigurationStore } = await import('./configuration-store')
            const configStore = useConfigurationStore.getState()
            const headers = get().getApiHeaders()
            await configStore.initBaseConfiguration(headers)
            logger.auth('Configuration initialized after auto-login')
          } catch (error) {
            logger.error('Failed to initialize configuration after auto-login', error)
          }

          return
        }

        const timeLeft = Number(expiresIn) - new Date().getTime() - 30000 // 30 Sekunden vorher

        if (timeLeft < 0) {
          return
        }

        clearTimeout(timer)
        timer = setTimeout(() => {
          get().refreshAuthToken()
        }, timeLeft)

        if (token && timeLeft) {
          get().setToken(
            token,
            expiresIn!,
            localStorage.getItem("refreshToken")!,
            localStorage.getItem("refreshTokenExpires")!
          )

          // Initialize configuration after auto-login
          try {
            const { useConfigurationStore } = await import('./configuration-store')
            const configStore = useConfigurationStore.getState()
            const headers = get().getApiHeaders()
            await configStore.initBaseConfiguration(headers)
            console.log('✅ Configuration initialized after auto-login')
          } catch (error) {
            console.error('❌ Error initializing configuration after auto-login:', error)
          }
        }
      },

      refreshAuthToken: async () => {
        const state = get()

        // Skip refresh in dev bypass mode
        const isDevBypass = localStorage.getItem('dev_bypass_mode') === 'true'
        if (isDevBypass) {
          logger.auth('Dev Bypass: Skipping token refresh')
          return
        }

        const timeLeft = Number(state.tokenExpires) - new Date().getTime() - 5 // 5 Sekunden vorher

        if (timeLeft < 0) {
          state.userLogout()
          return
        }

        const refreshUrl = ASSISTENT_API_URL + "token/refresh"
        const tokenRefreshDo = {
          refreshToken: state.refreshToken
        }

        try {
          const response = await axios.post(refreshUrl, tokenRefreshDo, {
            headers: {
              'X-AAI-APPLICATIONID': ASSISTENT_API_HEADER_KEY
            }
          })

          state.setToken(
            response.data.token,
            response.data.tokenExpire,
            response.data.refreshToken,
            response.data.refreshTokenExpire
          )
        } catch (error) {
          state.userLogout()
        }
      },

      // Getters
      getApiHeaders: () => {
        const state = get()
        return {
          'Authorization': state.token || '',
          'X-AAI-APPLICATIONID': ASSISTENT_API_HEADER_KEY
        }
      },

      getApiHeadersWithoutBaseContentType: () => {
        const state = get()
        return {
          'Authorization': state.token || '',
          'X-AAI-APPLICATIONID': ASSISTENT_API_HEADER_KEY,
          'Content-Type': 'application/json'
        }
      },

      isAuthenticated: () => {
        return get().token !== null
      },

      getToken: () => get().token,

      userEmail: () => {
        const state = get()
        if (state.decodedToken && state.decodedToken.email) {
          return state.decodedToken.email
        }
        return 'user@example.com'
      },

      getUserId: () => {
        return get().decodedToken?.id || ''
      },

      getFirstName: () => {
        return get().decodedToken?.firstname || ''
      },

      getLastName: () => {
        return get().decodedToken?.lastname || ''
      },

      getEmail: () => {
        return get().decodedToken?.sub || ''
      },

      isCustomer: () => {
        const decodedToken = get().decodedToken
        return decodedToken?.role === 'customer'
      },

      isAdmin: () => {
        const decodedToken = get().decodedToken
        return decodedToken?.role === 'admin'
      },

      hasRole: (role: string) => {
        const decodedToken = get().decodedToken
        if (!decodedToken || !decodedToken.roles) {
          return false
        }
        return decodedToken.roles.includes(role)
      },

      canSeeDeviceId: () => {
        const decodedToken = get().decodedToken
        if (!decodedToken) {
          return false
        }
        return decodedToken.role === 'customer' || decodedToken.role === 'admin'
      },

      canEdit: () => {
        const decodedToken = get().decodedToken
        if (!decodedToken) {
          return false
        }
        // Customers can edit, admins typically can too
        return decodedToken.role === 'customer' || decodedToken.role === 'admin'
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        tokenExpires: state.tokenExpires,
        refreshToken: state.refreshToken,
        refreshTokenExpires: state.refreshTokenExpires,
      }),
    }
  )
)

// React Hook for easy usage
export const useAuth = () => {
  const authStore = useAuthStore()

  return {
    user: authStore.user,
    isAuthenticated: authStore.isAuthenticated(),
    isLoading: false, // Add loading state if needed
    login: authStore.userLogin,
    logout: authStore.userLogout,
    token: authStore.getToken(),
    hasRole: authStore.hasRole,
  }
}
